import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BootstrapService } from 'app/service/bootstrap.service';
import { UserService } from 'app/service/user.service';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';

type PageState = 'loading' | 'ready';

// Zustimmung nachholen: bestehende Konten ohne Zustimmung und neue Fassungen der Rechtsdokumente.
// Solange sie fehlt, sperrt das Backend alles andere (HTTP 403 mit Code consent_required).
@Component({
  standalone: false,
  selector: 'app-consent',
  templateUrl: './consent.component.html',
  styleUrls: ['./consent.component.scss']
})
export class ConsentComponent implements OnInit {
  readonly currentYear = new Date().getFullYear();
  state: PageState = 'loading';
  form: FormGroup;
  submitted = false;
  isLoading = false;
  serverError = '';
  missing: string[] = [];
  participant = false;
  companyName = '';
  purpose = '';
  purposeText = '';
  selectedLanguage = 'en';

  supportedLanguages: { code: string; name: string }[] = [
    { code: 'de', name: 'Deutsch' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'pt', name: 'Português' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private userService: UserService,
    private bootstrapService: BootstrapService,
    private cookieService: CookieService,
    private router: Router
  ) {
    translate.setDefaultLang('en');
    this.form = this.formBuilder.group({ terms: [false], consent: [false] });
  }

  ngOnInit() {
    const stored = localStorage.getItem('selectedLanguage');
    this.selectedLanguage = this.supportedLanguages.some(l => l.code === stored) ? (stored as string) : 'en';
    this.translate.use(this.selectedLanguage);

    this.userService.getConsent().subscribe(
      status => {
        this.missing = status.missing || [];
        if (!this.missing.length) {
          this.finish();
          return;
        }
        this.participant = this.needsConsent;
        this.companyName = status.company_name || '';
        this.purpose = status.purpose || '';
        this.purposeText = status.purpose_text || '';
        // Pflichtfelder sind nur die fehlenden Dokumente
        if (this.needsTerms) {
          this.form.get('terms')?.setValidators(Validators.requiredTrue);
        }
        if (this.needsConsent) {
          this.form.get('consent')?.setValidators(Validators.requiredTrue);
        }
        this.form.updateValueAndValidity();
        this.state = 'ready';
      },
      () => this.router.navigate(['/login'])
    );
  }

  get needsTerms(): boolean {
    return this.missing.includes('terms_of_use');
  }

  get needsConsent(): boolean {
    return this.missing.includes('privacy_consent');
  }

  get consentsGiven(): boolean {
    return (!this.needsTerms || this.form.get('terms')?.value === true) &&
      (!this.needsConsent || this.form.get('consent')?.value === true);
  }

  changeLanguage(lang: string) {
    this.selectedLanguage = lang;
    this.translate.use(lang);
    localStorage.setItem('selectedLanguage', lang);
    this.userService.loadLang.next(lang);
  }

  submit() {
    this.submitted = true;
    this.serverError = '';
    if (!this.consentsGiven || this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.userService.giveConsent({
      terms_accepted: this.form.get('terms')?.value === true,
      consent_accepted: this.form.get('consent')?.value === true,
      language: this.selectedLanguage.toUpperCase()
    }).subscribe(
      () => this.finish(),
      () => {
        this.isLoading = false;
        this.serverError = this.translate.instant('acceptPage.errorGeneric');
      }
    );
  }

  logout() {
    localStorage.clear();
    this.cookieService.deleteAll('/');
    this.router.navigate(['/login']);
  }

  // Weiter wie nach dem Login: je nach Rolle auf die Startseite
  private finish() {
    this.isLoading = false;
    this.bootstrapService.bootstrap();
  }
}
